import React from "react";
import LPressable from "@components/LPressable";
import * as rudderstack from "@utils/rudderstack";
import { Link, LinkProps, router } from "expo-router";
import { GestureResponderEvent, Platform } from "react-native";

type Props = React.PropsWithChildren<LinkProps> &
  (
    | {
        pressable?: true;
        sentryEventName: string | null;
      }
    | {
        pressable: false;
        sentryEventName?: never;
      }
  );

const LLink = ({
  onPress,
  pressable = true,
  children,
  sentryEventName,
  ...rest
}: Props) => {
  const internalOnPress = (
    e: React.MouseEvent<HTMLAnchorElement, MouseEvent> | GestureResponderEvent,
  ) => {
    rudderstack.track("navigate", { value: Link.resolveHref(rest.href) });

    // On web, only intercept internal app navigation
    // Let the browser handle external links, new tabs, and modified clicks
    if (Platform.OS === "web") {
      const mouseEvent = e as React.MouseEvent<HTMLAnchorElement, MouseEvent>;
      const resolvedHref = Link.resolveHref(rest.href);

      // Check if click has modifier keys (Cmd/Ctrl+click for new tab, etc.)
      const hasModifierKey = mouseEvent.metaKey || mouseEvent.ctrlKey ||
                            mouseEvent.shiftKey || mouseEvent.altKey;

      // Check if link should open in new tab
      const opensInNewTab = rest.target === "_blank" ||
                           mouseEvent.currentTarget?.target === "_blank";

      // Check if href is external (starts with http:// or https://)
      const isExternal = typeof resolvedHref === "string" &&
                        /^https?:\/\//.test(resolvedHref);

      // Only intercept internal app links without modifiers
      if (!hasModifierKey && !opensInNewTab && !isExternal) {
        e.preventDefault();
        router.push(rest.href);
        onPress?.(e);
        return;
      }

      // For external/new-tab links, let browser handle it
      onPress?.(e);
      return;
    }

    onPress?.(e);
  };

  return (
    <Link {...rest} onPress={internalOnPress} asChild>
      {pressable && Platform.OS !== "web" ? (
        <LPressable sentryEventName={sentryEventName as string}>
          {children}
        </LPressable>
      ) : (
        children
      )}
    </Link>
  );
};

export default LLink;
